import {
  ApartmentDto,
  RegularUnitAdvertisementDto,
  SeniorUnitAdvertisementDto,
  UnitAdvertisementDto
} from "homey-housing-lottery-app-dto";
import {EligibilityCheckData, getRelevantApartmentsForIncomeAndSize, RelevantApartmentsResult} from "lottery-shared";
import {User} from "../services/user.service";
import {Preferences} from "./user";

export type Lottery = ApartmentDto;
export type UnitAdvertisementsWithType =
  | { type: 'regular', units: RegularUnitAdvertisementDto[] }
  | { type: 'senior', units: SeniorUnitAdvertisementDto[] };

export const getRelevantAdvertisements = (apartment: Lottery, user: User): RelevantApartmentsResult<UnitAdvertisementDto> => {
  switch (user.status) {
    case "unauthorized":
      return {status: 'eligible', advertisements: apartment.unitTypes};
    case "signup-flow": {
      const householdInfo = user.householdInfo;
      return householdInfo
        ? getRelevantApartmentsForIncomeAndSize<UnitAdvertisementDto>(
          apartment.unitTypes,
          {
            lowerHouseholdIncome: householdInfo.lowerIncome,
            upperHouseholdIncome: householdInfo.upperIncome,
            size: householdInfo.size,
            receivesRentalAssistance: user.receivesRentalAssistance,
            eligibleForSenior: user.eligibleForSenior,
          }
        )
        : {status: 'ineligible', reason: 'income-too-high'};
    }
    case "mandatory-flow": {
      return getRelevantApartmentsForIncomeAndSize<UnitAdvertisementDto>(
        apartment.unitTypes,
        {
          lowerHouseholdIncome: user.householdInfo.lowerIncome,
          upperHouseholdIncome: user.householdInfo.upperIncome,
          size: user.householdInfo.size,
          receivesRentalAssistance: user.receivesRentalAssistance,
          eligibleForSenior: user.eligibleForSenior,
        },
      );
    }
    case "active": {
      return getRelevantApartmentsForIncomeAndSize<UnitAdvertisementDto>(
        apartment.unitTypes,
        {
          lowerHouseholdIncome: user.householdInfo.lowerIncome,
          upperHouseholdIncome: user.householdInfo.upperIncome,
          size: user.householdInfo.size,
          receivesRentalAssistance: user.receivesRentalAssistance,
          eligibleForSenior: user.eligibleForSenior,
        },
      );
    }
  }
};

export const isUserEligibleForApartment = (apartment: Lottery, user: User | undefined): boolean => {
  return user === undefined ? false : (getRelevantAdvertisements(apartment, user).status === 'eligible' && apartment.status === 'open');
}

const isIncomeAndSizeEligibleForApartment = (
  apartment: Lottery,
  data: EligibilityCheckData
): boolean => {
  return getRelevantApartmentsForIncomeAndSize<UnitAdvertisementDto>(apartment.unitTypes, data).status === 'eligible';
}

export const filterRelevantApartmentsForIncomeAndSize = (
  apartments: Lottery[],
  data: EligibilityCheckData
) => {
  return apartments.filter(apartment =>
    isIncomeAndSizeEligibleForApartment(apartment, data)
  );
}

export function isApartmentPreferred(apartment: Lottery, preferences: Preferences): boolean {
  return isRoomSizePreferred(apartment, preferences) && isBoroughPreferred(apartment, preferences);
}

export const sortApartmentsByBestFit = (apartments: Lottery[], user: User): Lottery[] => {
  if (user === undefined || user.status === 'unauthorized') {
    return apartments;
  }

  if (user.preferences === undefined) {
    return apartments;
  }

  const preferences = user.preferences;
  return sortApartmentsByBestFitByPreferences(apartments, preferences);
}

export const sortApartmentsByBestFitByPreferences = (apartments: Lottery[], preferences: Preferences): Lottery[] => {
  return [...apartments].sort((a, b) => {
    const aPreferred = isApartmentPreferred(a, preferences);
    const bPreferred = isApartmentPreferred(b, preferences);

    if (aPreferred && !bPreferred) {
      return -1;
    }

    if (!aPreferred && bPreferred) {
      return 1;
    }

    return 0;
  });
}

const isRoomSizePreferred = (apartment: Lottery, preferences: Preferences) => {
  return apartment.unitTypes.some(
    unitType => {
      switch (unitType.unitType) {
        case 'studio':
          return preferences.room.includes('studio');
        case "one-bedroom":
          return preferences.room.includes('one-bedroom');
        case "two-bedrooms":
          return preferences.room.includes('two-bedrooms');
        case "three-bedrooms":
          return preferences.room.includes('three-bedrooms');
        case "four-bedrooms":
          return preferences.room.includes('four-or-more-bedrooms');
        case "five-bedrooms":
          return preferences.room.includes('four-or-more-bedrooms');
        case "six-bedrooms":
          return preferences.room.includes('four-or-more-bedrooms');
      }
    }
  )
}

const isBoroughPreferred = (apartment: Lottery, preferences: Preferences) => {
  return preferences.borough.includes(apartment.borough);
}
